import type {
  ContadorNotificacoes,
  EntradaAtualizarItemCatalogo,
  EntradaCriarItemCatalogo,
  EnvelopeListagem,
  EnvelopeOffset,
  ItemCatalogo,
  NomeCatalogo,
  Notificacao,
  RegistroAuditoria,
  ResultadoMarcarTodasLidas,
} from '@baiocchi/shared'

import { api, type ParametrosConsulta } from './cliente'

/**
 * Cinco recursos, **um** contrato (`03-endpoints-draft.md §4`):
 * `/pontos-conversao`, `/produtos-associados`, `/motivos-agendamento`,
 * `/motivos-venda-nao-realizada`, `/tags`.
 *
 * A API os trata como CRUD genérico, então a web também: uma função por operação,
 * parametrizada pelo nome do catálogo. Cinco telas quase idênticas escritas à mão
 * divergiriam na primeira correção — e são as telas que a operação usa para
 * consertar dado errado.
 *
 * Não existe `DELETE`: registro em uso nunca é excluído (RN-41). Desativar é a
 * operação, e histórico já gravado mantém o título antigo.
 *
 * Os tipos vêm de `@baiocchi/shared`, e não mais de `contratos-pendentes`: o módulo
 * `catalogos` da API existe e valida com os **mesmos** schemas Zod (RNF-121).
 */
export const catalogosApi = {
  listar(catalogo: NomeCatalogo, filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeOffset<ItemCatalogo>>(`/${catalogo}`, filtros, sinal)
  },

  /**
   * Só os ativos — é o modo que as telas de operação usam para preencher select
   * (§4: "`GET` sempre aceita `?ativo=true`"). Um select que oferece motivo
   * desativado produz `422 MOTIVO_INATIVO` no submit (RN-11A).
   */
  ativos(catalogo: NomeCatalogo, sinal?: AbortSignal) {
    return api.get<EnvelopeOffset<ItemCatalogo>>(
      `/${catalogo}`,
      { ativo: 'true', limite: 200, ordenar: 'ordem' },
      sinal,
    )
  },

  criar(catalogo: NomeCatalogo, entrada: EntradaCriarItemCatalogo) {
    return api.post<ItemCatalogo>(`/${catalogo}`, entrada)
  },

  atualizar(catalogo: NomeCatalogo, id: number, entrada: EntradaAtualizarItemCatalogo) {
    return api.patch<ItemCatalogo>(`/${catalogo}/${id}`, entrada)
  },

  desativar(catalogo: NomeCatalogo, id: number) {
    return api.post<ItemCatalogo>(`/${catalogo}/${id}/desativar`)
  },

  reativar(catalogo: NomeCatalogo, id: number) {
    return api.post<ItemCatalogo>(`/${catalogo}/${id}/reativar`)
  },

  /**
   * Reordenação em lote — uma requisição para a lista inteira, e não uma por item: se
   * uma falhasse no meio, a lista ficaria numa ordem que ninguém pediu. A API aplica em
   * transação.
   */
  reordenar(catalogo: NomeCatalogo, itens: ReadonlyArray<{ id: number; ordem: number }>) {
    return api.patch<{ atualizados: number }>(`/${catalogo}/ordem`, { itens })
  },
}

/**
 * `/notificacoes` — §11. Os tipos vêm de `@baiocchi/shared` desde que o módulo da
 * API nasceu (INT-06); antes disso moravam em `contratos-pendentes.ts`, que foi
 * apagado — não esvaziado — como o próprio arquivo mandava.
 */
export const notificacoesApi = {
  /** MVP: polling do contador (§11). O contrato de leitura não muda com SSE. */
  contador(sinal?: AbortSignal) {
    return api.get<ContadorNotificacoes>('/notificacoes/contador', undefined, sinal)
  },

  /** Paginação por cursor: a caixa cresce e item novo chega no topo enquanto se rola. */
  listar(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeListagem<Notificacao>>('/notificacoes', filtros, sinal)
  },

  marcarLida(uuid: string) {
    return api.post<void>(`/notificacoes/${uuid}/marcar-lida`)
  },

  marcarTodasLidas() {
    return api.post<ResultadoMarcarTodasLidas>('/notificacoes/marcar-todas-lidas')
  },
}

/**
 * `GET /auditoria` — §13.
 *
 * O tipo vem de `@baiocchi/shared`. Antes era escrito à mão aqui, e divergia da API
 * em quase todo campo (`ocorreuEm`/`criadoEm`, `recurso`/`entidade`,
 * `detalhes`/`valoresNovos`, e um `id` que a API não mandava). Como nada ligava as
 * duas pontas, a tela compilava, renderizava e não mostrava dado nenhum.
 */
export const auditoriaApi = {
  listar(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeListagem<RegistroAuditoria>>('/auditoria', filtros, sinal)
  },
}
